<template>
  <component
    :is="cellElement"
    class="sf-table-header-cell"
    :class="cellClass"
    :data-testid="`sf-header-${headerIndex}`"
  >
    <span
      :class="cellTextClass"
      :style="cellLinkStyle"
      data-testid="sf-header-cell-text"
    >
      <slot />
    </span>
    <i
      v-if="sortable"
      data-testid="sf-header-cell-arrow"
      :class="[styles.arrow, sortingArrow]"
    />
  </component>
</template>

<script setup lang="ts" generic="HD extends BaseHeader">
import type { BaseHeader } from './types'

import { computed } from 'vue'

import type { TableSortDirections, TableVariants } from './constants'
import { tableSortDirections, tableVariants } from './constants'
import { useTableStyles } from './use-cell-styles'

interface Props {
  align?: 'left' | 'right'
  headerId: string
  headerIndex: number
  maxHeaderCellWidth?: number | null
  sortable: boolean
  sortBy?: HD | ''
  sortDirection?: TableSortDirections
  variant?: TableVariants
}

const props = withDefaults(defineProps<Props>(), {
  align: 'left',
  maxHeaderCellWidth: undefined,
  sortable: true,
  sortBy: '',
  sortDirection: tableSortDirections.NONE,
  variant: tableVariants.DEFAULT,
})

const { styles } = useTableStyles(() => props.variant)

const cellElement = computed(() => {
  return props.sortable ? 'a' : 'span'
})

const cellClass = computed(() => {
  return {
    'sf-a': props.sortable,
    'sf-table-header-cell--align-right': props.align === 'right',
  }
})

const cellTextClass = computed(() => {
  return {
    'sf-inline-block': !!props.maxHeaderCellWidth,
    'sf-truncate': !!props.maxHeaderCellWidth,
    'sf-noscroll': !!props.maxHeaderCellWidth,
  }
})

const cellLinkStyle = computed(() => {
  return props.maxHeaderCellWidth
    ? {
        maxWidth: `${props.maxHeaderCellWidth - 20}px`,
      }
    : {}
})

const sortingArrow = computed(() => {
  const { headerId, sortBy, sortDirection, maxHeaderCellWidth } = props

  return {
    'fa fa-sort-up':
      headerId === sortBy && sortDirection === tableSortDirections.ASC,
    'fa fa-sort-down':
      headerId === sortBy && sortDirection === tableSortDirections.DESC,
    'fa fa-sort': headerId !== sortBy,
    'sf-inline-block': !!maxHeaderCellWidth,
  }
})
</script>

<style lang="scss">
.sf-table-header-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;

  &--align-right {
    justify-content: right;
  }
}
</style>
